import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, Self, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  SelectControlValueAccessor,
} from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';

import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccountGroup } from '@vendasta/account-group';
import { BusinessSelectorDialogService, FormatAddressToStringPipe } from '@vendasta/businesses';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { debounceTime, Observable, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { PARTNER_ID_TOKEN } from '../../../../core/src/lib/tokens';

export class SelectAccountControl extends FormControl<AccountGroup | string> {
  constructor(value?: AccountGroup | string) {
    super(value);
  }
}

@Component({
  selector: 'inbox-select-account',
  imports: [
    CommonModule,
    GalaxyFormFieldModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatIcon,
    MatInput,
    MatOption,
    TranslateModule,
    FormatAddressToStringPipe,
    ReactiveFormsModule,
    GalaxyEmptyStateModule,
  ],
  templateUrl: './inbox-select-account.component.html',
  styleUrl: './inbox-select-account.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useClass: SelectControlValueAccessor,
      multi: true,
    },
  ],
})
export class InboxSelectAccountComponent implements OnInit {
  control?: SelectAccountControl;
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  protected loading$: Observable<boolean>;
  protected accounts$: Observable<AccountGroup[]>;
  protected hasMore$: Observable<boolean>;
  protected loadingFirstPage: Signal<boolean>;

  constructor(
    private router: Router,
    private readonly businessSelectorDialogService: BusinessSelectorDialogService,
    @Self() readonly ngControl: NgControl,
  ) {
    this.loading$ = this.businessSelectorDialogService.loading$;
    this.accounts$ = this.businessSelectorDialogService.business$;
    this.hasMore$ = this.businessSelectorDialogService.hasMore$;

    this.businessSelectorDialogService.data = { partnerId: this.partnerId() || '', marketId: '' };

    this.loadingFirstPage = this.businessSelectorDialogService.loadingFirstPage;

    this.businessSelectorDialogService.initBusinesses();
  }

  ngOnInit() {
    if (!this.ngControl || !(this.ngControl.control instanceof SelectAccountControl)) {
      throw new Error('CustomInputComponent: No ControlValueAccessor provided');
    }
    this.control = this.ngControl.control;

    this.accounts$ = this.control.valueChanges.pipe(
      debounceTime(200),
      startWith(''),
      tap((term) => {
        const searchTerm = this.getSearchTerm(term);
        this.businessSelectorDialogService.searchFormFieldValue = searchTerm || '';
      }),
      switchMap(() => this.businessSelectorDialogService.business$),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getSearchTerm(term: string | AccountGroup): string {
    switch (typeof term) {
      case 'object':
        return term.accountGroupId || '';
      case 'string':
        return term;
    }
  }

  displayAccountGroup(accountGroup: AccountGroup): string {
    if (!accountGroup) return '';

    const companyName = accountGroup?.napData?.companyName;
    const address = accountGroup.napData.address;

    return [companyName, address].filter((s) => !!s).join('   ');
  }

  openCreateAccountForm() {
    this.router.navigateByUrl('/business/create');
  }
}
